/**
 * MarketHoursUtility.js
 * Utility functions for US stock market hours, holidays, and trading day calculations
 */

class MarketHoursUtility {
    /**
     * Check if a given date is a US market holiday
     */
    static isMarketHoliday(date) {
        const year = date.getFullYear();
        const month = date.getMonth(); // 0-indexed
        const day = date.getDate();
        const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
        
        // Weekends are not trading days
        if (dayOfWeek === 0 || dayOfWeek === 6) {
            return true;
        }
        
        // Major US market holidays (simplified list)
        const holidays = [
            // New Year's Day
            new Date(year, 0, 1),
            // Martin Luther King Jr. Day (3rd Monday in January)
            this.getNthWeekdayOfMonth(year, 0, 1, 3),
            // Presidents Day (3rd Monday in February)
            this.getNthWeekdayOfMonth(year, 1, 1, 3),
            // Good Friday (Friday before Easter)
            this.getGoodFriday(year),
            // Memorial Day (last Monday in May)
            this.getLastWeekdayOfMonth(year, 4, 1),
            // Juneteenth (June 19)
            new Date(year, 5, 19),
            // Independence Day (July 4)
            new Date(year, 6, 4),
            // Labor Day (1st Monday in September)
            this.getNthWeekdayOfMonth(year, 8, 1, 1),
            // Thanksgiving (4th Thursday in November)
            this.getNthWeekdayOfMonth(year, 10, 4, 4),
            // Christmas Day (December 25)
            new Date(year, 11, 25)
        ];
        
        // Check if current date matches any holiday
        return holidays.some(holiday => 
            holiday.getFullYear() === year &&
            holiday.getMonth() === month &&
            holiday.getDate() === day
        );
    }

    /**
     * Get the nth occurrence of a weekday in a month
     */
    static getNthWeekdayOfMonth(year, month, weekday, n) {
        const firstDay = new Date(year, month, 1);
        const firstWeekday = firstDay.getDay();
        const daysToAdd = (weekday - firstWeekday + 7) % 7;
        const targetDate = new Date(year, month, 1 + daysToAdd + (n - 1) * 7);
        return targetDate;
    }

    /**
     * Get the last occurrence of a weekday in a month
     */
    static getLastWeekdayOfMonth(year, month, weekday) {
        const lastDay = new Date(year, month + 1, 0); // Last day of the month
        const lastWeekday = lastDay.getDay();
        const daysToSubtract = (lastWeekday - weekday + 7) % 7;
        const targetDate = new Date(year, month + 1, 0 - daysToSubtract);
        return targetDate;
    }

    /**
     * Calculate Good Friday for a given year
     */
    static getGoodFriday(year) {
        // Easter calculation (simplified)
        const a = year % 19;
        const b = Math.floor(year / 100);
        const c = year % 100;
        const d = Math.floor(b / 4);
        const e = b % 4;
        const f = Math.floor((b + 8) / 25);
        const g = Math.floor((b - f + 1) / 3);
        const h = (19 * a + b - d - g + 15) % 30;
        const i = Math.floor(c / 4);
        const k = c % 4;
        const l = (32 + 2 * e + 2 * i - h - k) % 7;
        const m = Math.floor((a + 11 * h + 22 * l) / 451);
        const n = Math.floor((h + l - 7 * m + 114) / 31);
        const p = (h + l - 7 * m + 114) % 31;
        
        const easterDate = new Date(year, n - 1, p + 1);
        const goodFriday = new Date(easterDate);
        goodFriday.setDate(easterDate.getDate() - 2); // Good Friday is 2 days before Easter
        
        return goodFriday;
    }

    /**
     * Check if the market is currently open (9:30 AM - 4:00 PM ET on trading days)
     */
    static isMarketOpen(date = new Date()) {
        // Convert to Eastern Time
        const easternTime = new Date(date.toLocaleString("en-US", {timeZone: "America/New_York"}));
        
        // Check if it's a trading day
        if (this.isMarketHoliday(easternTime)) {
            return false;
        }
        
        const hours = easternTime.getHours();
        const minutes = easternTime.getMinutes();
        const timeInMinutes = hours * 60 + minutes;
        
        // Market hours: 9:30 AM (570 minutes) to 4:00 PM (960 minutes) ET
        const marketOpen = 9 * 60 + 30; // 9:30 AM
        const marketClose = 16 * 60; // 4:00 PM
        
        return timeInMinutes >= marketOpen && timeInMinutes < marketClose;
    }

    /**
     * Get the last trading day before the given date
     */
    static getLastTradingDay(date = new Date()) {
        const testDate = new Date(date);
        testDate.setDate(testDate.getDate() - 1); // Start with previous day
        
        // Keep going back until we find a trading day
        while (this.isMarketHoliday(testDate)) {
            testDate.setDate(testDate.getDate() - 1);
            
            // Safety check - don't go back more than 30 days
            if (Math.abs(testDate - date) > 30 * 24 * 60 * 60 * 1000) {
                console.warn('Could not find a trading day within 30 days');
                break;
            }
        }
        
        return testDate;
    }

    /**
     * Format date as YYYY-MM-DD for API calls
     */
    static formatDateForAPI(date) {
        return date.toISOString().split('T')[0];
    }

    /**
     * Check if a date is during extended trading hours (pre-market or after-hours)
     */
    static isExtendedTradingHours(date = new Date()) {
        // Convert to Eastern Time
        const easternTime = new Date(date.toLocaleString("en-US", {timeZone: "America/New_York"}));
        
        // Check if it's a trading day
        if (this.isMarketHoliday(easternTime)) {
            return false;
        }
        
        const hours = easternTime.getHours();
        const minutes = easternTime.getMinutes();
        const timeInMinutes = hours * 60 + minutes;
        
        // Pre-market: 4:00 AM - 9:30 AM ET
        const preMarketStart = 4 * 60; // 4:00 AM
        const marketOpen = 9 * 60 + 30; // 9:30 AM
        
        // After-hours: 4:00 PM - 8:00 PM ET
        const marketClose = 16 * 60; // 4:00 PM
        const afterHoursEnd = 20 * 60; // 8:00 PM
        
        return (timeInMinutes >= preMarketStart && timeInMinutes < marketOpen) ||
               (timeInMinutes >= marketClose && timeInMinutes < afterHoursEnd);
    }

    /**
     * Get market status string for logging/display
     */
    static getMarketStatus(date = new Date()) {
        if (this.isMarketHoliday(date)) {
            const dayOfWeek = date.getDay();
            if (dayOfWeek === 0 || dayOfWeek === 6) {
                return 'Weekend';
            } else {
                return 'Holiday';
            }
        } else if (this.isMarketOpen(date)) {
            return 'Open';
        } else if (this.isExtendedTradingHours(date)) {
            return 'Extended Hours';
        } else {
            return 'Closed';
        }
    }

    /**
     * Get the next trading day after the given date
     */
    static getNextTradingDay(date = new Date()) {
        const testDate = new Date(date);
        testDate.setDate(testDate.getDate() + 1); // Start with next day
        
        // Keep going forward until we find a trading day
        while (this.isMarketHoliday(testDate)) {
            testDate.setDate(testDate.getDate() + 1);
            
            // Safety check - don't go forward more than 30 days
            if (Math.abs(testDate - date) > 30 * 24 * 60 * 60 * 1000) {
                console.warn('Could not find a trading day within 30 days');
                break;
            }
        }
        
        return testDate;
    }
}

// Export for use in other modules
window.MarketHoursUtility = MarketHoursUtility;